import React, { useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import { useUISettings } from '../context/UISettingsContext';

interface LandingPageProps {
  onStart: (companyName: string) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onStart }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const { t } = useUISettings();

  const [companyName, setCompanyName] = useState('');
  const [showForm, setShowForm] = useState(false);

  useLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      gsap.set([badgeRef.current, titleRef.current, textRef.current, btnRef.current], { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: {
          ease: 'power4.out',
          force3D: true,
        },
      });

      tl.fromTo(badgeRef.current, { y: -30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, delay: 0.2 })
        .fromTo(titleRef.current, { y: 60, opacity: 0 }, { y: 0, opacity: 1, duration: 1 }, '-=0.5')
        .fromTo(textRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 }, '-=0.7')
        .fromTo(btnRef.current, { opacity: 0 }, { opacity: 1, duration: 0.45 }, '-=0.35');
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (companyName.trim()) {
      onStart(companyName);
    }
  };

  return (
    <div ref={containerRef} className="relative z-10 flex min-h-screen flex-col items-center justify-center overflow-hidden px-5">
      <div className="mb-12 max-w-4xl select-none text-center">
        <div ref={badgeRef} className="mb-5 flex justify-center opacity-0 will-change-transform">
          <div className="app-panel inline-flex items-center justify-center rounded-full border px-4 py-1.5 shadow-sm backdrop-blur-xl">
            <div className="mr-3 h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--app-accent-strong)]">{t('home.badge')}</span>
          </div>
        </div>

        <h1 ref={titleRef} className="mb-6 text-4xl font-black leading-[0.9] tracking-tighter text-[var(--app-text)] opacity-0 will-change-transform md:text-7xl">
          {t('home.titleLine1')} <br />
          <span className="font-serif text-[var(--app-accent)] italic font-light">{t('home.titleLine2')}</span>
        </h1>

        <p ref={textRef} className="mx-auto max-w-2xl text-base leading-relaxed text-[var(--app-text-muted)] opacity-0 will-change-transform md:text-lg">
          {t('home.description')}
        </p>
      </div>

      {!showForm ? (
        <button
          ref={btnRef}
          data-testid="home-init-scan-btn"
          onClick={() => setShowForm(true)}
          className="group relative overflow-hidden rounded-full bg-[var(--app-accent-strong)] px-8 py-4 text-white opacity-0 shadow-xl transition-transform duration-200 will-change-transform hover:scale-105 active:scale-95"
        >
          <div className="absolute inset-0 translate-y-full bg-[var(--app-accent)] transition-transform duration-300 ease-out group-hover:translate-y-0" />
          <span className="relative z-10 flex items-center text-xs font-black uppercase tracking-[0.14em]">
            {t('home.start')}
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </span>
        </button>
      ) : (
        <form className="app-panel w-full max-w-[21rem] rounded-[1.5rem] border px-6 py-7 shadow-2xl backdrop-blur-2xl" onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div>
              <label htmlFor="companyName" className="mb-2 ml-1 block text-[9px] font-black uppercase tracking-[0.14em] text-[var(--app-text-muted)]">
                {t('home.companyLabel')}
              </label>
              <input
                id="companyName"
                data-testid="home-company-input"
                type="text"
                autoComplete="off"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={t('home.companyPlaceholder')}
                maxLength={120}
                autoFocus
                className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] px-4 py-3 text-base font-bold text-[var(--app-text)] shadow-inner transition-all placeholder:text-[var(--app-text-soft)] focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              type="submit"
              data-testid="home-get-started-btn"
              disabled={!companyName.trim()}
              className="w-full rounded-xl bg-[var(--app-accent-strong)] px-8 py-3 text-white shadow-lg transition-all hover:bg-[var(--app-accent)] active:scale-95 disabled:grayscale disabled:opacity-30"
            >
              <span className="flex items-center justify-center text-[11px] font-black uppercase tracking-[0.14em]">
                {t('home.submit')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default LandingPage;
